import { withPriceSeperator } from '@flight-workspace/common-client/src/util/number';
import classNames from 'classnames/bind';
import { getDurationKr } from 'common/utils';
import { MutableRefObject } from 'react';
import styles from '../index.module.scss';
import { IIcon, IItem, IMarker, IMarkerOption } from '../types';
import { getNaverMaps } from '../utils';

const cx = classNames.bind(styles);

const CLASS_PLACE = cx('Place');
const CLASS_TOOLTIP = cx('Tooltip');
const CLASS_INFO = cx('info');
const CLASS_NAME = cx('name');
const CLASS_PRICE = cx('price');
const CLASS_DURATION = cx('duration');

interface IIconSize {
  width: number;
  height: number;
}

// 마커 아이콘의 margin Size
const SIZE_ICON_MARGIN = { width: 2.5, height: 10 };

// ZIndex 기준 (큰 아이콘은 작은 아이콘 보다 항상 위에 노출되어야 하므로 기준점을 달리한다.)
const ZINDEX_DETAIL_PLANE = 2000;
const ZINDEX_PLANE = 1000;

const generateDetailPlaneIcon = (
  cityName: string,
  { duration, stops, totalFare }: IItem,
  clickCode?: string,
) =>
  [
    `<div class=${CLASS_TOOLTIP} aria-expanded ${clickCode ? `data-event-nlog="click" data-event-area-code="${clickCode}"` : ``}>`,
    `<p class=${CLASS_INFO}>`,
    `<i class=${CLASS_NAME}>`,
    `${cityName}`,
    `</i>`,
    `<b class=${CLASS_PRICE}>`,
    `${withPriceSeperator(totalFare)}~`,
    `</b>`,
    `<span class=${CLASS_DURATION}>${stops ? `경유 ${stops}회` : '직항'}, ${getDurationKr(duration)}</span>`,
    `</p></div>`,
  ].join('');
const generatePlaneIcon = () =>
  `<div class=${CLASS_TOOLTIP} aria-expanded></div>`;
const generateRedDotIcon = () => `<div class=${CLASS_PLACE} />`;

// 마커 아래 원형 아이콘의 아래 반구 영역까지 아이콘 중복 영역으로 커버하기 위한 조정
const getAdjustedRect = (bounds: naver.maps.Bounds) => {
  const naverMaps = getNaverMaps();
  const newMinPoint = bounds.getMin().sub(0, 7); // 반구 높이가 7px
  return naverMaps.PointBounds(newMinPoint, bounds.getMax());
};

const getMarker = (markerOption: IMarkerOption): IMarker => {
  const naverMaps = getNaverMaps();
  const { map, item, trip } = markerOption;
  const {
    location: { latitude, longitude },
    cityName,
    rank,
  } = trip;

  const bigIcon = item
    ? generateDetailPlaneIcon(cityName, item, markerOption?.markerClickCode)
    : '';
  const redDotIcon = generateRedDotIcon();

  return new naverMaps.Marker({
    map,
    position: new naverMaps.LatLng(latitude, longitude),
    icon: {
      content: item ? bigIcon : redDotIcon,
      anchor: [0, 0],
      rank: item ? rank : 0,
      isBig: true,
    },
    cityName,
    zIndex: item ? ZINDEX_DETAIL_PLANE - rank : 0,
    bigContent: bigIcon,
    item,
    clickable: !!item,
  });
};

const setMarkersSize = (markers: IMarker[]) => {
  const { width: baseWidth, height: baseHeight } = SIZE_ICON_MARGIN;
  const iconSizes = new Map<string, IIconSize>();
  Array.from(document.getElementsByClassName(CLASS_INFO)).forEach(
    (marker: any) => {
      const cityName = marker.getElementsByClassName(CLASS_NAME)[0].innerText;
      const { width, height } = marker.getBoundingClientRect();
      iconSizes.set(cityName, { width, height });
    },
  );
  markers.forEach((marker: IMarker) => {
    const newIcon = marker.getIcon() as naver.maps.HtmlIcon;
    const iconSize = iconSizes.get(marker.cityName);
    if (!iconSize) return;
    const { width, height } = iconSize;
    newIcon.size = [width + baseWidth, height + baseHeight];
    marker.setIcon(newIcon);
  });
};

const setMarkerSmall = (marker: IMarker) => {
  const newIcon = marker.getIcon() as IIcon;
  newIcon.content = generatePlaneIcon();
  newIcon.isBig = false;
  marker.setIcon(newIcon);
};

const setMarkerBig = (marker: IMarker) => {
  const newIcon = marker.getIcon() as IIcon;
  newIcon.content = marker.bigContent;
  newIcon.isBig = true;
  marker.setIcon(newIcon);
};

const setMarkerRank = (marker: IMarker, rank: number) => {
  const newIcon = marker.getIcon() as IIcon;
  newIcon.rank = rank;
  marker.setIcon(newIcon);
  const newZIndex = (newIcon.isBig ? ZINDEX_DETAIL_PLANE : ZINDEX_PLANE) - rank;
  marker.setZIndex(newZIndex);
};

const getSortedMarkers = (markers: IMarker[]) => {
  return markers
    .sort((a, b) => a.getIcon().rank - b.getIcon().rank)
    .map((marker: IMarker, index: number) => {
      setMarkerRank(marker, index);
      return marker;
    });
};

// 마우스가 hover 되거나 click 되었을 때, 마커가 작은 아이콘이라면 큰 아이콘으로 변경
const setMarkerOnTop = (marker: IMarker, markers: IMarker[]) => {
  const sortedMarkers = getSortedMarkers(markers);

  setMarkerBig(marker);

  const currentDrawingRect = getAdjustedRect(marker.getDrawingRect());

  // 현재 마커를 제일 앞 번호(최고 우선 순위)로 옮긴다
  const index = sortedMarkers.indexOf(marker);
  const tempNewMarkers = [
    marker,
    ...sortedMarkers.slice(0, index),
    ...sortedMarkers.slice(index + 1),
  ];

  tempNewMarkers.forEach((targetMarker: IMarker, targetIndex: number) => {
    setMarkerRank(targetMarker, targetIndex);
    // 0번째(=현재(자기 자신) 마커)와의 비교는 제외
    if (targetIndex) {
      const tempDrawingRect = getAdjustedRect(targetMarker.getDrawingRect());
      const isMarkerBig = targetMarker.getIcon().isBig;

      if (isMarkerBig && tempDrawingRect.intersects(currentDrawingRect)) {
        setMarkerSmall(targetMarker);
        targetMarker.setZIndex(ZINDEX_PLANE - targetIndex);
      }
    }
  });
};

const setDestinationMarkersListener = (
  destinationMarkers: IMarker[],
  markerClickHandler: Function,
) => {
  const naverMaps = getNaverMaps();

  destinationMarkers.forEach((marker: IMarker) => {
    naverMaps.Event.addListener(marker, 'click', (e: any) => {
      if (marker.getIcon().isBig) {
        markerClickHandler(e, marker.item);
      } else {
        setMarkerOnTop(marker, destinationMarkers);
      }
    });
    naverMaps.Event.addListener(marker, 'mouseover', () => {
      if (!marker.getIcon().isBig) {
        setMarkerOnTop(marker, destinationMarkers);
      }
    });
  });
};

const handleOverlappedMarkers = (markers: IMarker[]) => {
  const sortedMarkers = getSortedMarkers(markers);

  sortedMarkers
    .map((marker: IMarker, index: number) => {
      setMarkerBig(marker);
      marker.setZIndex(ZINDEX_DETAIL_PLANE - index);
      return marker;
    })
    .forEach((marker: IMarker, index: number) => {
      const currentDrawingRect = getAdjustedRect(marker.getDrawingRect());

      // 본인보다 우선 순위가 높은 마커들의 drawingRects
      const drawingRects = sortedMarkers
        .slice(0, index)
        .map((tempMarker: IMarker) => {
          return {
            tempMarker,
            drawingRect: getAdjustedRect(tempMarker.getDrawingRect()),
          };
        });

      const isSafe = drawingRects.every(({ tempMarker, drawingRect }) => {
        // 우선순위가 높지만 이미 작은 마커라면 상관없음
        if (!tempMarker.getIcon().isBig) {
          return true;
        }
        return !drawingRect.intersects(currentDrawingRect);
      });

      if (!isSafe) {
        setMarkerSmall(marker);
        marker.setZIndex(ZINDEX_PLANE - index);
      }
      return marker;
    });
};

const addMarkers = ({
  departureMarkersRef,
  destinationMarkersRef,
  list,
  mapRef,
  markerClickCode,
}: {
  departureMarkersRef: MutableRefObject<IMarker[]>;
  destinationMarkersRef: MutableRefObject<IMarker[]>;
  list: IItem[];
  mapRef: MutableRefObject<naver.maps.Map | undefined>;
  markerClickCode?: string;
}) => {
  if (!mapRef.current) {
    return;
  }

  // 도착
  destinationMarkersRef.current = list
    .filter(({ destination: { cityName } }) => cityName)
    .map(item => {
      const { destination } = item;
      return getMarker({
        item,
        map: mapRef.current!,
        markerClickCode,
        trip: destination,
      });
    });

  const departureCities = new Set(list.map(item => item.departure));
  // 이륙
  departureMarkersRef.current = Array.from(departureCities).map(departure => {
    return getMarker({
      map: mapRef.current!,
      trip: departure,
    });
  });
};

const removeMarkers = ({
  departureMarkersRef,
  destinationMarkersRef,
}: {
  departureMarkersRef: MutableRefObject<IMarker[]>;
  destinationMarkersRef: MutableRefObject<IMarker[]>;
}) => {
  destinationMarkersRef.current.forEach(marker => marker.setMap(null));
  departureMarkersRef.current.forEach(marker => marker.setMap(null));
  destinationMarkersRef.current = [];
  departureMarkersRef.current = [];
};

export {
  addMarkers,
  handleOverlappedMarkers,
  removeMarkers,
  setDestinationMarkersListener,
  setMarkersSize,
};
